import { canEdit } from '../store.js';
import { renderMemberRoleSelect } from '../config/member-roles.js';
import { createTripInvite } from '../trip-invites.js';
import { bindAvatarEmojiPicker, readAvatarEmojiFromForm, renderAvatarEmojiPicker } from '../utils/avatar-emoji-picker.js';
import { escapeHtml } from '../utils/dom.js';
import { trapModalHistory } from '../utils/modal-history.js';

let releaseModalHistory = null;

function destroyInviteModal() {
  document.querySelector('#invite-modal')?.remove();
}

function destroyInviteModalFromBack() {
  releaseModalHistory = null;
  destroyInviteModal();
}

function closeModal() {
  const dismiss = releaseModalHistory;
  releaseModalHistory = null;
  if (dismiss) dismiss();
  else destroyInviteModal();
}

export function renderInviteFormHtml({ tripTitle, formId = 'invite-form' }) {
  return `
    <p class="invite-modal__hint">Aggiungi un collaboratore a <strong>${escapeHtml(tripTitle)}</strong>. Riceverà un link per creare l'account (o accedere) e unirsi automaticamente.</p>
    <form class="custom-form" id="${formId}">
      <label class="field">
        <span class="field-label">Email</span>
        <input class="field-input" type="email" name="email" required placeholder="nome@esempio.it" />
      </label>
      <label class="field">
        <span class="field-label">Nome in app</span>
        <input class="field-input" name="displayName" required placeholder="es. Laura" maxlength="40" />
      </label>
      <div class="field">
        <span class="field-label">Emoji</span>
        ${renderAvatarEmojiPicker({ name: 'avatarEmoji' })}
      </div>
      <label class="field">
        <span class="field-label">Ruolo</span>
        ${renderMemberRoleSelect('memberRole', 'editor')}
      </label>
      <p class="invite-modal__error status-error" id="invite-form-error" hidden></p>
      <div class="custom-form__actions">
        <button type="submit" class="btn-primary">Genera link invito</button>
        <button type="button" class="btn-ghost" data-invite-cancel>Annulla</button>
      </div>
    </form>
    <div class="invite-modal__result" id="invite-result" hidden>
      <p class="invite-modal__success">Link pronto — invialo alla persona invitata:</p>
      <div class="invite-modal__link-row">
        <input class="field-input invite-modal__link" id="invite-url" readonly />
        <button type="button" class="btn-primary btn-primary--sm" id="invite-copy">Copia</button>
      </div>
      <p class="invite-modal__note">Valido 14 giorni. L'invitato deve registrarsi o accedere con la stessa email.</p>
      <button type="button" class="btn-ghost" data-invite-done>Chiudi</button>
    </div>
  `;
}

export function bindInviteForm(container, { tripId, instanceId, onCreated, onCancel, formId = 'invite-form' }) {
  bindAvatarEmojiPicker(container);

  container.querySelector('[data-invite-cancel]')?.addEventListener('click', () => onCancel?.());
  container.querySelector('[data-invite-done]')?.addEventListener('click', () => onCancel?.());

  const form = container.querySelector(`#${CSS.escape(formId)}`);
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errEl = container.querySelector('#invite-form-error');
    errEl.hidden = true;

    const fd = new FormData(e.target);
    const submitBtn = e.target.querySelector('[type="submit"]');
    submitBtn.disabled = true;

    try {
      const invite = await createTripInvite({
        tripId,
        instanceId,
        email: String(fd.get('email')),
        displayName: String(fd.get('displayName')),
        avatarEmoji: readAvatarEmojiFromForm(e.target),
        role: String(fd.get('memberRole') || 'editor'),
      });

      onCreated?.();

      e.target.hidden = true;
      const result = container.querySelector('#invite-result');
      const urlInput = container.querySelector('#invite-url');
      urlInput.value = invite.url;
      result.hidden = false;

      container.querySelector('#invite-copy')?.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(invite.url);
          container.querySelector('#invite-copy').textContent = 'Copiato!';
        } catch {
          urlInput.select();
          document.execCommand('copy');
        }
      });
    } catch (error) {
      errEl.textContent = error.message;
      errEl.hidden = false;
    } finally {
      submitBtn.disabled = false;
    }
  });
}

export function openInviteModal({ tripId, instanceId, tripTitle, onCreated }) {
  if (!canEdit()) {
    alert('Inviti disponibili solo per gli editor del viaggio.');
    return;
  }

  closeModal();

  const backdrop = document.createElement('div');
  backdrop.id = 'invite-modal';
  backdrop.className = 'invite-modal';
  backdrop.innerHTML = `
    <div class="invite-modal__dialog" role="dialog" aria-labelledby="invite-modal-title" aria-modal="true">
      <button type="button" class="invite-modal__close" aria-label="Chiudi">×</button>
      <h2 id="invite-modal-title" class="invite-modal__title">Invita al viaggio</h2>
      ${renderInviteFormHtml({ tripTitle })}
    </div>
  `;

  document.body.appendChild(backdrop);
  releaseModalHistory = trapModalHistory(destroyInviteModalFromBack);

  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) closeModal();
  });
  backdrop.querySelector('.invite-modal__close')?.addEventListener('click', closeModal);

  bindInviteForm(backdrop, {
    tripId,
    instanceId,
    onCreated,
    onCancel: closeModal,
  });
}
